/**
 * AI Generation Panel
 *
 * Reusable collapsible panel for AI-assisted content generation.
 * Can be used across different forms that need LLM-powered generation.
 *
 * Features:
 * - Collapsible panel with purple gradient styling
 * - Model selector from available chat models
 * - Optional custom input section
 * - Generate button with loading state
 * - Explanation display after generation
 */

import React, { useState } from 'react';
import { Sparkles, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useChatModelsQuery } from '@/lib/queries/useModelsQuery';

export interface AiGenerationPanelProps {
  /** Title displayed in the panel header */
  title: string;
  /** Description shown when panel is expanded */
  description?: string;
  /** Button text for the generate action */
  generateButtonText?: string;
  /** Text shown while generating */
  generatingText?: string;
  /** Whether the panel is disabled */
  disabled?: boolean;
  /** Whether generation is in progress */
  isGenerating?: boolean;
  /** Callback when generate is clicked, receives selected model */
  onGenerate: (model: string) => void | Promise<void>;
  /** AI explanation text to display after generation */
  explanation?: string;
  /** Optional validation function, returns error message if invalid */
  validateBeforeGenerate?: () => string | null;
  /** Custom content to render in the panel (e.g., additional inputs) */
  children?: React.ReactNode;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
  /** Select model label */
  selectModelLabel?: string;
  /** Select model placeholder */
  selectModelPlaceholder?: string;
  /** AI explanation title */
  explanationTitle?: string;
}

export const AiGenerationPanel: React.FC<AiGenerationPanelProps> = ({
  title,
  description,
  generateButtonText = 'Generate',
  generatingText = 'Generating...',
  disabled = false,
  isGenerating = false,
  onGenerate,
  explanation,
  validateBeforeGenerate,
  children,
  defaultCollapsed = true,
  selectModelLabel = 'Select Model',
  selectModelPlaceholder = 'Select a model...',
  explanationTitle = 'AI Analysis',
}) => {
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const [selectedModel, setSelectedModel] = useState('');
  const [localGenerating, setLocalGenerating] = useState(false);

  // Get available chat models
  const { data: chatModels = [] } = useChatModelsQuery();

  const handleGenerate = async () => {
    // Validate model selection
    if (!selectedModel) {
      alert(selectModelPlaceholder);
      return;
    }

    // Run custom validation if provided
    if (validateBeforeGenerate) {
      const error = validateBeforeGenerate();
      if (error) {
        alert(error);
        return;
      }
    }

    try {
      setLocalGenerating(true);
      await onGenerate(selectedModel);
    } catch (err) {
      console.error('AI generation failed:', err);
      alert('AI generation failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLocalGenerating(false);
    }
  };

  const generating = isGenerating || localGenerating;

  return (
    <div className="border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
      {/* Header - Always visible, clickable to toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full px-4 py-3 text-left"
        disabled={disabled}
      >
        <Sparkles className="w-5 h-5 text-purple-500" />
        <span className="flex-1 font-medium text-purple-800">{title}</span>
        <ChevronDown
          className={`w-4 h-4 text-purple-500 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Description */}
          {description && <p className="text-sm text-purple-700">{description}</p>}

          {/* Model Selector */}
          <div>
            <label className="block text-xs font-medium text-purple-700 mb-1">
              {selectModelLabel}
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
              disabled={disabled || generating}
            >
              <option value="">{selectModelPlaceholder}</option>
              {chatModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.id}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Children (additional inputs) */}
          {children}

          {/* Generate Button */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleGenerate}
              disabled={disabled || generating || !selectedModel}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {generatingText}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generateButtonText}
                </>
              )}
            </Button>
          </div>

          {/* AI Explanation */}
          {explanation && (
            <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
              <div className="flex items-start gap-2">
                <span className="text-purple-500">✨</span>
                <div>
                  <p className="text-xs font-medium text-purple-800 mb-1">{explanationTitle}</p>
                  <p className="text-sm text-gray-700">{explanation}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AiGenerationPanel;
